/**
 * useIntakeQueue — Composition hook
 *
 * Joins Won opportunities, IntakeRecords, and OpsGroups to produce
 * a unified intake queue with derived display statuses.
 */

import { useMemo } from 'react';
import { useWonOpportunities, useProductsSold } from '@entities/opportunity';
import type { Opportunity } from '@entities/opportunity';
import { useIntakeRecords } from '@entities/intake-record';
import type { IntakeRecord } from '@entities/intake-record';
import { useOpsGroups } from '@entities/ops-group';
import type { OpsGroup } from '@entities/ops-group';

export type DisplayStatus = 'new' | 'in-review' | 'enrolled' | 'linked' | 'dismissed';

export interface IntakeQueueItem {
  opportunity: Opportunity;
  intakeRecord?: IntakeRecord;
  existingGroup?: OpsGroup;
  displayStatus: DisplayStatus;
  /**
   * What the rep actually sold, from the close form. Falls back to the
   * opportunity's Products to Quote when the close record has no sold data
   * yet, so the column is never blank for an older opp; `productsSource` says
   * which one you are looking at.
   */
  products: string[];
  productsSource: 'sold' | 'quoted';
}

export interface IntakeQueueResult {
  items: IntakeQueueItem[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  // Summary counts
  newCount: number;
  inReviewCount: number;
  enrolledCount: number;
  linkedCount: number;
  dismissedCount: number;
}

export function useIntakeQueue(): IntakeQueueResult {
  const wonOpps = useWonOpportunities();
  const intakeRecords = useIntakeRecords();
  const opsGroups = useOpsGroups();
  const productsSold = useProductsSold();

  const items = useMemo(() => {
    if (!wonOpps.data) return [];

    // Index intake records by opportunityId for fast lookup
    const intakeByOppId = new Map<string, IntakeRecord>();
    for (const rec of intakeRecords.data ?? []) {
      if (rec.opportunityId) intakeByOppId.set(rec.opportunityId, rec);
    }

    // Index OpsGroups by accountId (Dynamics Account UUID)
    const groupByAccountId = new Map<string, OpsGroup>();
    for (const g of opsGroups.data ?? []) {
      if (g.accountId) groupByAccountId.set(g.accountId, g);
    }

    return wonOpps.data.map((opp): IntakeQueueItem => {
      const intakeRecord = intakeByOppId.get(opp.id);
      // opp.groupId (_dts_group_value) is a Dynamics Account UUID, match against OpsGroup.accountId
      const existingGroup = (opp.groupId ? groupByAccountId.get(opp.groupId) : undefined)
        ?? (opp.accountId ? groupByAccountId.get(opp.accountId) : undefined);

      const sold = productsSold.data?.get(opp.id);
      const hasSold = Boolean(sold && sold.length > 0);

      let displayStatus: DisplayStatus;
      if (existingGroup && (!intakeRecord || intakeRecord.status === 'in-review')) {
        displayStatus = 'enrolled';
      } else if (intakeRecord) {
        displayStatus = intakeRecord.status === 'in-review' ? 'in-review'
          : intakeRecord.status === 'linked' ? 'linked'
          : 'dismissed';
      } else {
        displayStatus = 'new';
      }

      return {
        opportunity: opp,
        intakeRecord,
        existingGroup,
        displayStatus,
        products: hasSold ? sold! : opp.productsToQuote,
        productsSource: hasSold ? 'sold' : 'quoted',
      };
    });
  }, [wonOpps.data, intakeRecords.data, opsGroups.data, productsSold.data]);

  const counts = useMemo(() => {
    const c = { newCount: 0, inReviewCount: 0, enrolledCount: 0, linkedCount: 0, dismissedCount: 0 };
    for (const item of items) {
      if (item.displayStatus === 'new') c.newCount++;
      else if (item.displayStatus === 'in-review') c.inReviewCount++;
      else if (item.displayStatus === 'enrolled') c.enrolledCount++;
      else if (item.displayStatus === 'linked') c.linkedCount++;
      else if (item.displayStatus === 'dismissed') c.dismissedCount++;
    }
    return c;
  }, [items]);

  return {
    items,
    // productsSold is part of the loading gate on purpose: rendering the
    // Products Sold column from the quoted fallback and then swapping it when
    // the close data lands is worse than waiting for it.
    isLoading: wonOpps.isLoading || intakeRecords.isLoading || opsGroups.isLoading
      || productsSold.isLoading,
    isError: wonOpps.isError || intakeRecords.isError || opsGroups.isError,
    refetch: () => {
      wonOpps.refetch();
      intakeRecords.refetch();
      opsGroups.refetch();
      productsSold.refetch();
    },
    ...counts,
  };
}
